import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  type ActionSweep,
  type DetectorRow,
  type DetectorSiteRow,
  useActionSweep,
  useDetectors,
} from '@/lib/admin-actions'
import { formatExact, formatRate } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Age, age, hours, StatusWord, Tag, Th, Tile } from '../../-parts'

export const Route = createFileRoute('/_app/admin/actions/_list/detectors')({
  component: DetectorsPage,
})

/**
 * Whether every check is still running, everywhere it should be.
 *
 * The action list cannot answer this. A detector that has been failing for a
 * week and a site with nothing wrong produce the same empty list, and a
 * detector whose feeds are dark produces a third thing again — it records that
 * it declined to run rather than that it found nothing. The three read
 * differently here on purpose.
 */
function DetectorsPage() {
  const { data, isPending } = useDetectors()
  const sweep = useActionSweep()

  const run = (name: ActionSweep, label: string) =>
    sweep.mutate(name, {
      onSuccess: () => toast.success(`${label} queued`),
      onError: (error) => toast.error(error instanceof Error ? error.message : 'Failed to queue'),
    })

  if (isPending || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  const now = Date.parse(data.now)
  const failed = data.detectors.reduce((total, row) => total + row.failed, 0)
  const skipped = data.detectors.reduce((total, row) => total + row.skipped, 0)
  const never = data.detectors.reduce((total, row) => total + row.neverRun, 0)
  const stalest = data.detectors.reduce<number | null>(
    (worst, row) =>
      row.stalestHours === null
        ? worst
        : worst === null
          ? row.stalestHours
          : Math.max(worst, row.stalestHours),
    null,
  )

  return (
    <div className="space-y-4">
      {/* The one control here that addresses the deployment rather than a site.
          Per-site triggers live on Feeds beside the site they act on; "run
          everything" has no site to sit beside, and it belongs on the screen
          that says whether everything is running. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={sweep.isPending}
          onClick={() => run('action-scan-all', 'Scan of every site')}
        >
          Run every check, every site
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={sweep.isPending}
          onClick={() => run('action-review-all', 'Re-measure of every site')}
        >
          Re-measure every site
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Tile label="Failed" value={formatExact(failed)} tone={failed > 0 ? 'bad' : undefined} />
        <Tile
          label="Skipped"
          value={formatExact(skipped)}
          tone={skipped > 0 ? 'warn' : undefined}
        />
        <Tile label="Never run" value={formatExact(never)} />
        <Tile label="Stalest" value={hours(stalest) ?? 'never'} />
      </div>

      {data.detectors.map((detector) => (
        <Detector key={detector.type} detector={detector} now={now} />
      ))}
    </div>
  )
}

const SEVERITY: Record<string, number> = { failed: 0, skipped: 1, never: 2, ok: 3 }

function severityOf(site: DetectorSiteRow): number {
  return SEVERITY[site.status ?? 'never'] ?? 3
}

function Detector({ detector, now }: { detector: DetectorRow; now: number }) {
  const attention = detector.failed + detector.skipped + detector.neverRun
  const [open, setOpen] = useState(attention > 0)

  const sites = [...detector.sites].sort(
    (a, b) => severityOf(a) - severityOf(b) || (b.ageHours ?? 0) - (a.ageHours ?? 0),
  )

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-card">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border bg-secondary/40 px-4 py-2.5">
        <span className="font-mono text-[12.5px]">{detector.type}</span>
        <span className="text-[13px] text-muted-foreground">{detector.label}</span>
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          <Tag>{detector.nature}</Tag>
          {/* What has to have written something before this check means
              anything, which is what `skipped` below is a consequence of. */}
          {detector.requires.map((feed) => (
            <Tag key={feed}>{feed}</Tag>
          ))}
        </span>
      </header>

      <div className="flex flex-wrap gap-x-8 gap-y-3 px-4 py-3">
        <Figure label="ok" value={formatExact(detector.ok)} />
        <Figure
          label="skipped"
          value={formatExact(detector.skipped)}
          tone={detector.skipped > 0 ? 'warn' : undefined}
        />
        <Figure
          label="failed"
          value={formatExact(detector.failed)}
          tone={detector.failed > 0 ? 'bad' : undefined}
        />
        <Figure label="never run" value={formatExact(detector.neverRun)} muted />

        <span className="hidden w-px self-stretch bg-border sm:block" aria-hidden />

        <Figure label="last run" value={age(detector.lastRunAt, now)} />
        <Figure label="stalest" value={hours(detector.stalestHours) ?? 'never'} />

        <span className="hidden w-px self-stretch bg-border sm:block" aria-hidden />

        <Figure label="candidates" value={formatExact(detector.candidates)} />
        <Figure label="opened" value={formatExact(detector.opened)} />
        <Figure label="refreshed" value={formatExact(detector.refreshed)} />
        <Figure label="closed" value={formatExact(detector.closed)} />

        <span className="hidden w-px self-stretch bg-border sm:block" aria-hidden />

        <Figure label="actions" value={formatExact(detector.actions)} />
        <Figure label="enriched" value={formatExact(detector.enriched)} />
        <Figure label="suppressed" value={formatExact(detector.suppressed)} />
        {/* Suppressed over enriched. Null while nothing has been enriched, and
            a rate over nothing is not a rate, so the figure goes rather than
            standing there as a dash. */}
        {detector.suppressionRate !== null && (
          <Figure
            label="veto rate"
            value={formatRate(detector.suppressionRate, 0)}
            tone={detector.suppressionRate >= 0.5 ? 'warn' : undefined}
          />
        )}
      </div>

      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-2 border-t border-border px-4 py-1.5 text-left transition-colors hover:bg-secondary/40"
      >
        <span className="mono-label text-muted-foreground">{open ? '−' : '+'} sites</span>
        <span className="font-mono text-[11px] text-muted-foreground tabular">
          {formatExact(detector.sites.length)}
        </span>
      </button>

      {open && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-border bg-secondary/30">
                <Th align="left">Site</Th>
                <Th align="left" className="w-[130px]">
                  Status
                </Th>
                <Th className="w-[70px]">Age</Th>
                <Th className="w-[90px]">Candidates</Th>
                <Th className="w-[70px]">Opened</Th>
                <Th className="w-[80px]">Refreshed</Th>
                <Th className="w-[70px]">Closed</Th>
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <SiteRow key={site.siteId} site={site} now={now} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Four states, and the fourth is the absence of the other three.
 *
 * `ok` is the ordinary middle and stays grey; `skipped` and `failed` are the
 * two the screen exists to surface; and a check that has never run against a
 * site gets a hollow dot, because a filled one of any colour would claim it
 * reported something.
 */
function SiteRow({ site, now }: { site: DetectorSiteRow; now: number }) {
  const status = site.status ?? 'never run'

  return (
    <tr className="border-b border-border last:border-b-0">
      <td className="py-2 pr-3 pl-3">
        <span className="block font-mono text-[12.5px]">{site.host}</span>
        <span className="block text-[11px] text-muted-foreground">{site.organization}</span>
        {site.error && (
          <span className="block pt-0.5 font-mono text-[11.5px] text-destructive">
            {site.error}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <StatusWord
          label={status}
          hollow={site.status === null}
          tone={
            site.status === 'failed'
              ? 'bad'
              : site.status === 'skipped'
                ? 'warn'
                : site.status === 'ok'
                  ? 'plain'
                  : 'muted'
          }
        />
      </td>
      <td className="px-3 py-2 text-right">
        <Age at={site.startedAt} now={now} />
      </td>
      <Count value={site.candidates} />
      <Count value={site.opened} />
      <Count value={site.refreshed} />
      <Count value={site.closed} />
    </tr>
  )
}

function Count({ value }: { value: number }) {
  return (
    <td className="px-3 py-2 text-right">
      <span
        className={cn('font-mono text-[12px] tabular', value === 0 && 'text-muted-foreground/50')}
      >
        {formatExact(value)}
      </span>
    </td>
  )
}

function Figure({
  label,
  value,
  tone,
  muted,
}: {
  label: string
  value: string
  tone?: 'warn' | 'bad'
  muted?: boolean
}) {
  return (
    <div className="min-w-[64px]">
      <p className="mono-label text-muted-foreground">{label}</p>
      <p
        className={cn(
          'pt-1 font-mono text-[15px] leading-none tabular',
          muted && 'text-muted-foreground',
          tone === 'warn' && 'text-warning',
          tone === 'bad' && 'text-destructive',
        )}
      >
        {value}
      </p>
    </div>
  )
}
